<!--
 #TODO: add an horizontal scrolling and keep splis selected.
-->
<script setup lang="ts">
import * as d3 from "d3";
import { nextTick, onBeforeUnmount, onMounted, useTemplateRef, watch } from "vue";

import { remap } from "@/services/utils";

const { splits, datasetSize } = defineProps<{
  splits: number[][];
  datasetSize: number;
}>();
const selectedSplitIndex = defineModel<number>("selectedSplitIndex", { required: true });

const plot = useTemplateRef<SVGElement>("plot");

const margin = { top: 10, right: 30, bottom: 20, left: 30 };

function updatePlot() {
  if (plot.value) {
    const width = plot.value.clientWidth;
    const height = plot.value.clientHeight;
    const splitWidth = Math.ceil(
      Math.min(32, (width - margin.right - margin.left) / splits.length - 8)
    );

    const svg = d3.select(plot.value);

    // axes
    const axesG = svg.select<SVGGElement>(".axes");
    const xAxisG = axesG.select<SVGGElement>(".x");
    const yAaxisG = axesG.select<SVGGElement>(".y");
    const xScale = d3
      .scaleLinear()
      .domain([0, splits.length - 1])
      .range([margin.left + splitWidth * 1.618, width - margin.right - splitWidth * 1.618]);
    xAxisG
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(d3.axisBottom(xScale).ticks(splits.length));

    const longestSplit = splits.reduce((a, b) => (b.length > a.length ? b : a), []);
    const yScale = d3
      .scaleLinear()
      .domain([0, longestSplit.length])
      .range([height - margin.bottom, margin.top]);

    yAaxisG.attr("transform", `translate(${margin.left}, 0)`).call(
      d3
        .axisLeft(yScale)
        .tickSize(0)
        .tickFormat((d) => {
          return Math.round(
            remap(
              d.valueOf(),
              0,
              longestSplit.length,
              0,
              datasetSize > 0 ? datasetSize : longestSplit.length
            )
          ).toString();
        })
    );

    axesG.selectAll(".domain").attr("stroke-width", 0);

    // grid
    const gridG = svg.select<SVGGElement>(".grid");

    gridG
      .selectAll("line.v")
      .data(xScale.ticks())
      .enter()
      .append("line")
      .attr("class", "v")
      .attr("x1", (d) => xScale(d))
      .attr("x2", (d) => xScale(d))
      .attr("y1", margin.top)
      .attr("y2", height - margin.bottom);

    gridG
      .selectAll("line.h")
      .data(yScale.ticks())
      .enter()
      .append("line")
      .attr("class", "h")
      .attr("x1", margin.right)
      .attr("x2", width - margin.left)
      .attr("y1", (d) => yScale(d))
      .attr("y2", (d) => yScale(d));

    // "bars" that represent a split.
    // each split a list of values between 0 and 1
    // each of those represents a bucket of indices in the dataset
    // the closest the value is to one
    // the highest the amount of sample has been used as test data
    const root = getComputedStyle(document.documentElement);
    const trainColor = root.getPropertyValue("--color-brand-blue-500").trim();
    const testColor = root.getPropertyValue("--color-brand-orange-500").trim();
    const color = d3.interpolateRgb(trainColor, testColor);
    const splitsData = [];
    for (const s of splits) {
      splitsData.push(
        s.map((d, i) => {
          const stroke = color(d);
          const fill = d3.hsl(stroke).copy({ l: 0.97 }).toString();
          return {
            y: i,
            h: 1,
            fill,
            stroke,
          };
        })
      );
    }

    interface InnerRectDatum {
      y: number;
      h: number;
      fill: string;
      stroke: string;
    }
    const splitsG = svg.select<SVGGElement>(".splits");
    const splitBars = splitsG.selectAll<SVGGElement, InnerRectDatum>("g.split").data(splitsData);
    splitBars
      .enter()
      .append("g")
      .attr("class", "split")
      .merge(splitBars)
      .attr("transform", (_, i) => `translate(${xScale(i)}, 0)`)
      .attr("data-split-index", (_, i) => i)
      .classed("selected", (_, i) => i === selectedSplitIndex.value)
      .exit()
      .remove();

    // draw inside bars
    const innerRects = splitBars.selectAll<SVGRectElement, InnerRectDatum>("rect").data((d) => d);
    innerRects
      .enter()
      .append("rect")
      .merge(innerRects)
      .attr("x", -splitWidth / 2)
      .attr("y", (d) => yScale(d.y + d.h))
      .attr("width", splitWidth)
      .attr("height", (d) => yScale(d.y) - yScale(d.y + d.h) + 1) // add +1 to avoid hairlines between rect.
      .attr("fill", (d) => d.fill)
      .attr("stroke", (d) => d.stroke)
      .attr("stroke-dasharray", (d) => [0, splitWidth, yScale(d.y) - yScale(d.y + d.h) + 1, 0]);

    // draw strokes
    const [a, b] = yScale.domain();
    const strokes = splitBars.selectAll<SVGLineElement, InnerRectDatum>("line").data((d) => [
      { ...d[0]!, y: a! },
      { ...d[d.length - 1]!, y: b! },
    ]);
    strokes
      .enter()
      .append("line")
      .merge(strokes)
      .attr("class", "top-stroke")
      .attr("x1", -splitWidth / 2)
      .attr("y1", (d) => yScale(d.y))
      .attr("x2", splitWidth / 2)
      .attr("y2", (d) => yScale(d.y))
      .attr("stroke", (d) => d.stroke);

    // interactivity
    splitBars.on("click", (e: Event) => {
      const element = e.currentTarget as SVGGElement;
      if (element) {
        const newSelectedIndex = parseInt(element.dataset.splitIndex!);
        if (!isNaN(newSelectedIndex) && newSelectedIndex < splits.length) {
          selectedSplitIndex.value =
            newSelectedIndex !== selectedSplitIndex.value ? newSelectedIndex : -1;
          splitBars.classed("selected", (_, i) => i === selectedSplitIndex.value);
        }
      }
    });
  }
}

const resizeObserver = new ResizeObserver(() => {
  updatePlot();
});

onMounted(async () => {
  // be sure that container has its full size already
  await nextTick();
  updatePlot();
  if (plot.value) {
    resizeObserver.observe(plot.value);
  }
});

onBeforeUnmount(() => {
  if (plot.value) {
    resizeObserver.unobserve(plot.value);
  }
});

watch(selectedSplitIndex, updatePlot);
</script>

<template>
  <div class="cross-validation-report-view-split-selector">
    <svg ref="plot" class="split-selector">
      <g class="grid"></g>
      <g class="axes">
        <g class="x"></g>
        <g class="y"></g>
      </g>
      <g class="splits"></g>
    </svg>
  </div>
</template>

<style>
.cross-validation-report-view-split-selector {
  .split-selector {
    width: 100%;
    height: 280px;

    text {
      color: var(--content-secondary);
      font-family: GeistMono, monospace;
    }

    .grid line {
      stroke: var(--border-tertiary);
      stroke-dasharray: 4 4;
      stroke-width: 1px;
    }

    .splits {
      .split {
        cursor: pointer;
        transition:
          filter var(--animation-duration) var(--animation-easing),
          opacity var(--animation-duration) var(--animation-easing);
      }

      &:has(.split.selected) {
        .split:not(.selected) {
          filter: grayscale(1);
          opacity: 0.5;
        }
      }
    }
  }
}
</style>
